import { Box, IconButton, Popover, Tooltip } from '@mui/material';
import React, { useCallback, useRef, useState } from 'react';
import AltIcon from './AltIcon';
import CircleTakeIcon from './CircleTakeIcon';
import { SelectType } from 'gql/graphql';

type SelectIconGenericProps = {
    selectType: SelectType
    onCircleClick: () => void
    onAltClick: () => void
    disabledCircle?: boolean
    disabledCircleTooltip?: string
}

export default function SelectIconWithHover({
    selectType,
    onCircleClick,
    onAltClick,
    disabledCircle,
    disabledCircleTooltip,
}: SelectIconGenericProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const currentlyHovering = useRef(false);

    const handlePopoverOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        if (anchorEl !== event.currentTarget) {
            setAnchorEl(event.currentTarget);
        }
    }, [anchorEl]);

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const handleHover = useCallback(() => {
        currentlyHovering.current = true;
    }, []);

    const handleCloseHover = useCallback(() => {
        currentlyHovering.current = false;
        setTimeout(() => {
            if (!currentlyHovering.current) {
                handlePopoverClose();
            }
        }, 50);
    }, []);

    const renderSelectIcon = (select: SelectType) => {
        const iconProps = {
            'aria-owns': open ? 'select-popover' : undefined,
            'aria-haspopup': 'true',
        }

        let icon = <CircleTakeIcon gray {...iconProps} />;

        if (select === SelectType.Select)
            icon = <CircleTakeIcon {...iconProps} />;
        if (select === SelectType.Alt)
            icon = <AltIcon {...iconProps} />;

        return (
            <Tooltip title={disabledCircle ? (disabledCircleTooltip || '') : ''} disableHoverListener={!disabledCircle}>
                <div
                    onMouseEnter={handlePopoverOpen}
                    onMouseOver={handleHover}
                    onMouseLeave={handleCloseHover}
                    style={{
                        opacity: disabledCircle ? 0.5 : 1,
                        cursor: disabledCircle ? 'not-allowed' : 'pointer',
                    }}
                >
                    {icon}
                </div>
            </Tooltip>
        );
    }

    return (
        <>
            {renderSelectIcon(selectType)}
            <Popover
                id='select-popover'
                sx={{ pointerEvents: 'none' }}
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClose={handlePopoverClose}
                disableRestoreFocus
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        pointerEvents: 'auto',
                    }}
                    onMouseOver={handleHover}
                    onMouseLeave={handleCloseHover}
                >
                    <Tooltip title={disabledCircle ? (disabledCircleTooltip || '') : ''} disableHoverListener={!disabledCircle}>
                        <span>
                            <IconButton
                                aria-label='Circle take'
                                onClick={disabledCircle ? undefined : onCircleClick}
                                disabled={disabledCircle}
                            >
                                <CircleTakeIcon gray={disabledCircle} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <IconButton
                        aria-label='Alt take'
                        onClick={onAltClick}
                    >
                        <AltIcon />
                    </IconButton>
                </Box>
            </Popover>
        </>
    );
}
